import { Box, Theme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SIDE_PANEL_DEFAULT_WIDTH, ZOOM_VALUES } from '../../../../constants/templates';
import useDebouncedCallback from '../../../../hooks/useDebouncedCallback';
import usePopover from '../../../../hooks/usePopover';
import useResizablePanel from '../../../../hooks/useResizablePanel';
import {
  redoTemplateBuilderChange,
  setTemplateValidationModal,
  undoTemplateBuilderChange,
  updateActivePage,
} from '../../../../store/actions/templates';
import {
  selectCanRedo,
  selectCanUndo,
  selectClaimLinkBuilderDraft,
  selectTemplateValidationErrors,
  selectTemplateValidationModal,
} from '../../../../store/selectors/templates';
import {
  ClaimLinkEditorPageEnum,
  ClaimLinkEditorViewTypeEnum,
  IExperienceInitPayload,
  IframeExperienceEventsEnum,
  IframeStatusEnum,
  IframeStepEnum,
  TemplateValidationModalEnum,
} from '../../../../types/templates';
import { extractPreviewValues, getIframeStepByPage } from '../../../../utils/templates';
import { IFRAME_EXPERIENCE_MOCK_DATA } from '../../../../_mock/iframe-experience';
import { TemplateManagerModal } from '../../../modals';
import { TemplateBuilderErrorModal } from '../../_dialogs_';
import { EditorHeader, EditorToolbar, Preview, SidePanel } from './components';
import { useExperienceBuilderPreview } from './hooks/useExperienceBuilderPreview';
import { useValidationErrorClearing } from './hooks/useValidationErrorClearing';

const getSidePanelMaxWidth = (containerWidth: number) => containerWidth / 2;

const resizeHandleStyles = (theme: Theme) => ({
  width: theme.spacing(1.25),
  flexShrink: 0,
  cursor: 'col-resize',
  alignSelf: 'stretch',
  ml: -1,
  mr: 2,
});

const dragOverlayStyles = (theme: Theme) => ({
  position: 'fixed',
  inset: 0,
  // Sit above the preview iframe (and any MUI-stacked surface) so the drag keeps tracking.
  zIndex: theme.zIndex.modal + 1,
  cursor: 'col-resize',
});

const TemplateLinkForm: React.FC = () => {
  const {
    width: sidePanelWidth,
    containerRef,
    panelRef: sidePanelColumnRef,
    isHandleActive: isResizeHandleActive,
    isDragging,
    handleProps: resizeHandleProps,
  } = useResizablePanel({ defaultWidth: SIDE_PANEL_DEFAULT_WIDTH, getMaxWidth: getSidePanelMaxWidth });

  const sidePanelBodyStyles = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      borderRight: '2px solid',
      borderRightColor: isResizeHandleActive ? 'grey.400' : 'transparent',
      '& > *': {
        flex: 1,
        minHeight: 0,
      },
    }),
    [isResizeHandleActive],
  );

  const dispatch = useDispatch();
  const templateDraft = useSelector(selectClaimLinkBuilderDraft);
  const validationErrors = useSelector(selectTemplateValidationErrors);
  const validationModal = useSelector(selectTemplateValidationModal);
  const { activePageId } = templateDraft;

  const canUndo = useSelector(selectCanUndo(activePageId));
  const canRedo = useSelector(selectCanRedo(activePageId));

  const [selectedZoom, setSelectedZoom] = useState<number>(ZOOM_VALUES.Normal);
  const [viewType, setViewType] = useState<ClaimLinkEditorViewTypeEnum>(ClaimLinkEditorViewTypeEnum.Desktop);

  const { isOpen, handleOpen, handleClose } = usePopover();
  const { shouldShowError, handleFieldChange, initializeFieldValue } = useValidationErrorClearing({
    validationErrors,
    validationModal,
    activePageId,
  });

  const previewValues = useMemo(() => (templateDraft ? extractPreviewValues(templateDraft) : null), [templateDraft]);

  const initData = useMemo<IExperienceInitPayload | null>(() => {
    if (!previewValues) {
      return null;
    }
    return {
      data: {
        ...IFRAME_EXPERIENCE_MOCK_DATA,
        experience: {
          ...IFRAME_EXPERIENCE_MOCK_DATA.experience,
          ...previewValues.experience,
        },
      },
      page: templateDraft?.activePageId ? getIframeStepByPage(templateDraft.activePageId) : IframeStepEnum.PickYourGift,
    };
  }, [previewValues, templateDraft?.activePageId]);

  const isInitSentRef = useRef(false);

  const handlePreviewReset = useCallback(() => {
    isInitSentRef.current = false;
  }, []);

  const {
    iframeRef,
    status,
    handleIframeLoad,
    handleIframeError,
    handleRetry,
    sendInit,
    setExperience,
    navigate,
    handleOpenInNewTab,
    postMessageToNewTab,
  } = useExperienceBuilderPreview({ getNewTabInitPayload: () => initData, onReset: handlePreviewReset });

  const debouncedSetExperience = useDebouncedCallback(setExperience, 500);
  const debouncedSetExperienceNewTab = useDebouncedCallback(postMessageToNewTab, 500);

  const isPreviewReady = useMemo(() => status === IframeStatusEnum.Ready, [status]);

  useEffect(() => {
    if (!isPreviewReady || !initData || isInitSentRef.current) {
      return;
    }
    isInitSentRef.current = true;
    sendInit(initData);
  }, [isPreviewReady, initData, sendInit]);

  useEffect(() => {
    if (!previewValues) {
      return;
    }

    if (isPreviewReady) {
      debouncedSetExperience(previewValues);
    }

    debouncedSetExperienceNewTab(IframeExperienceEventsEnum.SetExperience, previewValues);
  }, [previewValues, isPreviewReady, debouncedSetExperience, debouncedSetExperienceNewTab]);

  useEffect(() => {
    if (!isPreviewReady || !templateDraft?.activePageId) {
      return;
    }
    navigate({ step: getIframeStepByPage(templateDraft.activePageId) });
  }, [isPreviewReady, templateDraft?.activePageId, navigate]);

  const handleTabChange = useCallback(
    (newTab: ClaimLinkEditorPageEnum) => {
      dispatch(updateActivePage(newTab));
    },
    [dispatch],
  );

  const handleUndo = useCallback(() => {
    dispatch(undoTemplateBuilderChange(activePageId));
  }, [dispatch, activePageId]);

  const handleRedo = useCallback(() => {
    dispatch(redoTemplateBuilderChange(activePageId));
  }, [dispatch, activePageId]);

  const handleCloseErrorModal = useCallback(() => {
    dispatch(setTemplateValidationModal(TemplateValidationModalEnum.None));
  }, [dispatch]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <EditorHeader
        activeTab={activePageId}
        onTabChange={handleTabChange}
        shouldShowError={shouldShowError}
        onOpenTemplateDefaults={handleOpen}
      />
      <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
        <Box
          ref={sidePanelColumnRef}
          sx={{ display: 'flex', flexDirection: 'column', width: sidePanelWidth, flexShrink: 0, height: '100%' }}
        >
          <Box sx={sidePanelBodyStyles}>
            <SidePanel
              shouldShowError={shouldShowError}
              handleFieldChange={handleFieldChange}
              initializeFieldValue={initializeFieldValue}
            />
          </Box>
        </Box>
        <Box {...resizeHandleProps} sx={resizeHandleStyles} data-action="side_panel_resize_handle" />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <EditorToolbar
            viewType={viewType}
            onViewTypeChange={setViewType}
            zoom={selectedZoom}
            onZoomChange={setSelectedZoom}
            onUndo={canUndo ? handleUndo : undefined}
            onRedo={canRedo ? handleRedo : undefined}
          />
          <Preview
            iframeRef={iframeRef}
            viewType={viewType}
            zoom={selectedZoom}
            status={status}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            onRetry={handleRetry}
            onOpenInNewTab={handleOpenInNewTab}
          />
        </Box>
      </Box>
      <TemplateManagerModal open={isOpen} onClose={handleClose} isEditMode />
      <TemplateBuilderErrorModal
        open={validationModal === TemplateValidationModalEnum.Error}
        onClose={handleCloseErrorModal}
        errors={validationErrors || undefined}
      />
      {isDragging && <Box sx={dragOverlayStyles} data-testid="resize-overlay" />}
    </Box>
  );
};

export default TemplateLinkForm;
